import React, { lazy } from 'react';
import { createBrowserRouter, Navigate } from 'react-router';
import { RootLayout } from './layouts/RootLayout';
import { TabLayoutV2 } from './layouts/TabLayoutV2';
import { NotFound } from './components/NotFound';

// ── V2 screens (primary) ──
const HomeScreenV2 = lazy(() => import('./components/v2/HomeScreenV2').then(m => ({ default: m.HomeScreenV2 })));
const MomsScreenV2 = lazy(() => import('./components/v2/MomsScreenV2').then(m => ({ default: m.MomsScreenV2 })));
const EventsScreenV2 = lazy(() => import('./components/v2/EventsScreenV2').then(m => ({ default: m.EventsScreenV2 })));
const MyAccountScreenV2 = lazy(() => import('./components/v2/MyAccountScreenV2').then(m => ({ default: m.MyAccountScreenV2 })));
const UserProfileScreenV2 = lazy(() => import('./components/v2/UserProfileScreenV2').then(m => ({ default: m.UserProfileScreenV2 })));
const ChatsScreenV2 = lazy(() => import('./components/v2/ChatsScreenV2').then(m => ({ default: m.ChatsScreenV2 })));
const NotificationsScreenV2 = lazy(() => import('./components/v2/NotificationsScreenV2').then(m => ({ default: m.NotificationsScreenV2 })));

// ── V1 screens (not yet replaced) ──
const LandingScreen = lazy(() => import('./components/LandingScreen').then(m => ({ default: m.LandingScreen })));
const RadarScreen = lazy(() => import('./components/RadarScreen').then(m => ({ default: m.RadarScreen })));
const ForumScreen = lazy(() => import('./components/ForumScreen').then(m => ({ default: m.ForumScreen })));
const ExternalEventsScreen = lazy(() => import('./components/ExternalEventsScreen').then(m => ({ default: m.ExternalEventsScreen })));
const EventDetailScreen = lazy(() => import('./components/EventDetailScreen').then(m => ({ default: m.EventDetailScreen })));
const MarketFullScreen = lazy(() => import('./components/MarketFullScreen').then(m => ({ default: m.MarketFullScreen })));
const ChatsScreen = lazy(() => import('./components/ChatsScreen').then(m => ({ default: m.ChatsScreen })));
const ChatConversationScreen = lazy(() => import('./components/ChatConversationScreen').then(m => ({ default: m.ChatConversationScreen })));
const AddEventScreen = lazy(() => import('./components/AddEventScreen').then(m => ({ default: m.AddEventScreen })));
const EventEditScreen = lazy(() => import('./components/EventEditScreen').then(m => ({ default: m.EventEditScreen })));
const AddQuestionScreen = lazy(() => import('./components/AddQuestionScreen').then(m => ({ default: m.AddQuestionScreen })));
const AddListingScreen = lazy(() => import('./components/AddListingScreen').then(m => ({ default: m.AddListingScreen })));
const EditProfileScreen = lazy(() => import('./components/EditProfileScreen').then(m => ({ default: m.EditProfileScreen })));
const MyListingsScreen = lazy(() => import('./components/MyListingsScreen').then(m => ({ default: m.MyListingsScreen })));
const MyQuestionsScreen = lazy(() => import('./components/MyQuestionsScreen').then(m => ({ default: m.MyQuestionsScreen })));
const SavedScreen = lazy(() => import('./components/SavedScreen').then(m => ({ default: m.SavedScreen })));
const SettingsScreen = lazy(() => import('./components/SettingsScreen').then(m => ({ default: m.SettingsScreen })));
const SettingsNotificationsScreen = lazy(() => import('./components/SettingsNotificationsScreen').then(m => ({ default: m.SettingsNotificationsScreen })));
const ChangePasswordScreen = lazy(() => import('./components/ChangePasswordScreen').then(m => ({ default: m.ChangePasswordScreen })));
const HelpScreen = lazy(() => import('./components/HelpScreen').then(m => ({ default: m.HelpScreen })));
const ContactsScreen = lazy(() => import('./components/ContactsScreen').then(m => ({ default: m.ContactsScreen })));
const NotificationsPage = lazy(() => import('./components/NotificationsPage').then(m => ({ default: m.NotificationsPage })));
const QuestionDetailScreen = lazy(() => import('./components/QuestionDetailScreen').then(m => ({ default: m.QuestionDetailScreen })));
const PublicQuestionPage = lazy(() => import('./components/forum/PublicQuestionPage').then(m => ({ default: m.PublicQuestionPage })));
const QuestionEditScreen = lazy(() => import('./components/QuestionEditScreen').then(m => ({ default: m.QuestionEditScreen })));
const ListingDetailScreen = lazy(() => import('./components/ListingDetailScreen').then(m => ({ default: m.ListingDetailScreen })));
const MyEventsScreen = lazy(() => import('./components/MyEventsScreen').then(m => ({ default: m.MyEventsScreen })));
const SavedUsersScreen = lazy(() => import('./components/SavedUsersScreen').then(m => ({ default: m.SavedUsersScreen })));
const OnboardingScreen1 = lazy(() => import('./components/onboarding/OnboardingScreen1').then(m => ({ default: m.OnboardingScreen1 })));
const OnboardingScreen2 = lazy(() => import('./components/onboarding/OnboardingScreen2').then(m => ({ default: m.OnboardingScreen2 })));
const OnboardingScreen3 = lazy(() => import('./components/onboarding/OnboardingScreen3').then(m => ({ default: m.OnboardingScreen3 })));
const RegisterStep1 = lazy(() => import('./components/auth').then(m => ({ default: m.RegisterStep1 })));
const RegisterStep2 = lazy(() => import('./components/auth').then(m => ({ default: m.RegisterStep2 })));
const RegisterStep3 = lazy(() => import('./components/auth').then(m => ({ default: m.RegisterStep3 })));
const RegisterStep4 = lazy(() => import('./components/auth').then(m => ({ default: m.RegisterStep4 })));
const LoginScreen = lazy(() => import('./components/auth').then(m => ({ default: m.LoginScreen })));
const LoginCodeScreen = lazy(() => import('./components/auth').then(m => ({ default: m.LoginCodeScreen })));
const ForgotPasswordScreen = lazy(() => import('./components/auth').then(m => ({ default: m.ForgotPasswordScreen })));
const AuthCallbackScreen = lazy(() => import('./components/auth').then(m => ({ default: m.AuthCallbackScreen })));
const TermsOfService = lazy(() => import('./components/legal').then(m => ({ default: m.TermsOfService })));
const PrivacyPolicy = lazy(() => import('./components/legal').then(m => ({ default: m.PrivacyPolicy })));
const Disclaimer = lazy(() => import('./components/legal').then(m => ({ default: m.Disclaimer })));
const CommunityGuidelines = lazy(() => import('./components/legal').then(m => ({ default: m.CommunityGuidelines })));
const DataConsent = lazy(() => import('./components/legal').then(m => ({ default: m.DataConsent })));
const LegalIndex = lazy(() => import('./components/legal').then(m => ({ default: m.LegalIndex })));
const UnsubscribeRedirect = lazy(() => import('./components/UnsubscribeRedirect').then(m => ({ default: m.UnsubscribeRedirect })));

export const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    errorElement: <NotFound />,
    children: [
      { index: true, element: <LandingScreen /> },
      // Onboarding
      { path: 'onboarding/1', element: <OnboardingScreen1 /> },
      { path: 'onboarding/2', element: <OnboardingScreen2 /> },
      { path: 'onboarding/3', element: <OnboardingScreen3 /> },
      { path: 'onboarding/4', element: <Navigate to="/onboarding/3" replace /> },
      { path: 'onboarding/5', element: <Navigate to="/onboarding/3" replace /> },
      // TabBar routes — V2 design
      {
        element: <TabLayoutV2 />,
        children: [
          { path: 'home', element: <HomeScreenV2 /> },
          { path: 'moms', element: <MomsScreenV2 /> },
          { path: 'moms/radar', element: <RadarScreen /> },
          { path: 'events', element: <EventsScreenV2 /> },
          { path: 'external-events', element: <ExternalEventsScreen /> },
          { path: 'forum', element: <ForumScreen /> },
          // Profile — внутри TabLayoutV2, чтобы оставался нижний таб-бар
          { path: 'my-account', element: <MyAccountScreenV2 /> },
        ],
      },
      // Profile aliases
      { path: 'my-profile', element: <Navigate to="/my-account" replace /> },
      { path: 'profile', element: <Navigate to="/my-account" replace /> },
      // Остальные экраны
      { path: 'events/:id', element: <EventDetailScreen /> },
      { path: 'events/:id/edit', element: <EventEditScreen /> },
      { path: 'market', element: <MarketFullScreen /> },
      { path: 'chats', element: <ChatsScreenV2 /> },
      { path: 'chat-conversation/:id', element: <ChatConversationScreen /> },
      { path: 'add-event', element: <AddEventScreen /> },
      { path: 'add-question', element: <AddQuestionScreen /> },
      { path: 'add-listing', element: <AddListingScreen /> },
      { path: 'edit-profile', element: <EditProfileScreen /> },
      { path: 'my-listings', element: <MyListingsScreen /> },
      { path: 'my-questions', element: <MyQuestionsScreen /> },
      { path: 'my-events', element: <MyEventsScreen /> },
      { path: 'saved', element: <SavedScreen /> },
      { path: 'saved-users', element: <SavedUsersScreen /> },
      { path: 'settings', element: <SettingsScreen /> },
      { path: 'settings/notifications', element: <SettingsNotificationsScreen /> },
      { path: 'settings/change-password', element: <ChangePasswordScreen /> },
      { path: 'help', element: <HelpScreen /> },
      { path: 'contacts', element: <ContactsScreen /> },
      { path: 'notifications', element: <NotificationsScreenV2 /> },
      // Legal
      { path: 'legal/terms', element: <TermsOfService /> },
      { path: 'legal/privacy', element: <PrivacyPolicy /> },
      { path: 'legal/disclaimer', element: <Disclaimer /> },
      { path: 'legal/guidelines', element: <CommunityGuidelines /> },
      { path: 'legal/consent', element: <DataConsent /> },
      { path: 'legal/index', element: <LegalIndex /> },
      // Auth
      { path: 'register/step1', element: <RegisterStep1 /> },
      { path: 'register/step2', element: <RegisterStep2 /> },
      { path: 'register/step3', element: <RegisterStep3 /> },
      { path: 'register/step4', element: <RegisterStep4 /> },
      { path: 'login', element: <LoginScreen /> },
      { path: 'login/code', element: <LoginCodeScreen /> },
      { path: 'forgot-password', element: <ForgotPasswordScreen /> },
      { path: 'auth/callback', element: <AuthCallbackScreen /> },
      // Old email unsubscribe links pointed at SPA — redirect to edge function handler
      { path: 'unsubscribe', element: <UnsubscribeRedirect /> },
      // Public (unauthenticated) share links
      { path: 'q/:id', element: <PublicQuestionPage /> },
      // Detail screens
      { path: 'question/:id', element: <QuestionDetailScreen /> },
      { path: 'edit-question/:id', element: <QuestionEditScreen /> },
      { path: 'listing/:id', element: <ListingDetailScreen /> },
      { path: 'users/:id', element: <UserProfileScreenV2 /> },
      // 404
      { path: '*', element: <NotFound /> },
    ],
  },
]);
